import moment from 'moment';
import { chain, Dictionary, zipObject } from 'lodash';
import { ApaStatus } from '../types/graphql-global-types';
import { removeAccentsAndUpperCase } from '../common/logics';

export const getHours = (second: number) => Math.floor(second / 3600);
export const getMinutes = (second: number) => Math.floor((second % 3600) / 60);
export const getSecond = (second: number) => Math.floor(second % 60);

export const transformName = (name: string) =>
  name.charAt(0).toUpperCase() +
  name.substring(1, name.length).toLocaleLowerCase();

export const formatName = ({
  firstName,
  lastName,
}: {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
}) => {
  if (firstName && lastName) {
    return `${transformName(firstName)} ${transformName(
      lastName,
    ).toUpperCase()}`;
  }

  if (!firstName && lastName) {
    return transformName(lastName);
  }

  if (!lastName && firstName) {
    return transformName(firstName);
  }

  return '';
};

export const formatDate = (date: Date | undefined) =>
  moment(date).format(moment.HTML5_FMT.DATE);

export const sortAsc = <T>(array: T[], key: keyof T) =>
  array?.sort((a, b) => {
    const [u, v] = [a[key], b[key]];
    if (typeof u === 'string' && typeof v === 'string')
      return u.localeCompare(v);
    if (typeof u === 'number' && typeof v === 'number') return +u - +v;
    return -1;
  }) ?? [];

export const groupBy = <T>(
  array: T[] | null,
  value: (item: T) => unknown,
  keys?: string[],
): Dictionary<string | T[]>[] =>
  chain(array)
    .groupBy(value)
    .toPairs()
    .map((_) =>
      zipObject<string | T[]>(keys?.length === 2 ? keys : ['name', 'items'], _),
    )
    .value(); // return {name: string; items: T[]}[]

export const join = <T>(array: T[] | null, key: keyof T) =>
  (array ?? [])?.reduce<string>((acc, team, index) => {
    let temp = acc;
    if (index !== 0) temp += ', ';
    return temp + ((team && team[key]) ?? '');
  }, '');

export const capitalizeFirstLetter = (text: string | null | undefined) => {
  if (!text) return '';
  return `${text[0].toUpperCase()}${text.slice(1).toLowerCase()}`;
};

export const mapApaStatus = (apaStatus: ApaStatus | null | undefined) => {
  switch (apaStatus) {
    case ApaStatus.INDEPENDANT:
      return 'Indépendant';

    case ApaStatus.SALARIE:
      return 'Salarié';

    default:
      return '';
  }
};

export const removeAccents = (text: string | null | undefined): string =>
  (text ?? '').normalize('NFD').replace(/\p{Diacritic}/gu, '');

export const findSubstring = (
  text: string | null | undefined,
  predicate: string | null | undefined,
): boolean =>
  removeAccents(text)
    .toLowerCase()
    .includes(removeAccents(predicate).toLowerCase());

export const getInitials = (text: string | null | undefined) =>
  text
    ?.match(/(\b\S)?/g)
    ?.join('')
    ?.toUpperCase() ?? '';

/**
 * Retrieve nested item from object.
 * eg.: const myObject = {user: {firstName: 'John', lastName: 'Doe'}}
 * assert(getValue(myObject, 'user.firstName') === 'John', true).
 * @param {Object} data
 * @param {String} path dot separated
 * @returns {*}
 */
export const getValue = (
  data: { [p: string]: any } | null | undefined,
  path: string,
): any => {
  const paths = path.split('.');

  return paths.reduce<any>((acc, p) => {
    if (acc) acc = acc[p];
    return acc;
  }, data);
};

export const localCompareAICI = (
  a: string | null | undefined,
  b: string | null | undefined,
): number =>
  removeAccentsAndUpperCase(a || '').localeCompare(
    removeAccentsAndUpperCase(b || ''),
  );
